import "./BartrackSetup.scss";
import { useState, useEffect } from "react";
import FormField from "../../../../components/formField/FormField";
import GradientButton from "../../../../components/buttons/gradientButton/GradientButton";
import GreenRedOutlinedButtons from "../../../../components/buttons/greenRedOutlinedButtons/GreenRedOutlinedButtons";
import { Loader2 } from "../../../../helper/loader";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { getCollection } from "../../../../utility/getCollection";
import getBartrackLines from "../../../../utility/getBartrackLines";
import updateBartracks from "../../../../utility/updateBartracks";
import { useNavigate } from "react-router-dom";

function BartrackSetup({ org }) {
  const [bartracks, setBartracks] = useState([]);
  const [lines, setLines] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({ bartracks: [], lines: [] });
  const [currentOrgId, setCurrentOrgId] = useState();
  const { t } = useTranslation("bartrack");
  const location = useLocation();
  const selectedOrgId = location.state?.orgId || org?.id;
  const navigate = useNavigate();

  useEffect(() => {
    if (selectedOrgId) {
      setCurrentOrgId(selectedOrgId);
      fetchBartrack(selectedOrgId);
    } else {
      navigate(-1);
    }
  }, [selectedOrgId]);

  const bartracksValidation = yup.array().of(
    yup.object().shape({
      name: yup.string().required(t("name_required")),
      imageUrl: yup.string().nullable(),
      pressureLimit: yup.number().nullable(),
    }),
  );
  const linesValidation = yup.array().of(
    yup.object().shape({
      name: yup.string().required(t("name_required")),
      profitabilityIndex: yup.number(t("profitabilityIndex_number")).nullable(),
      imageUrl: yup.string().nullable(),
      bartrackUUID: yup
        .string()
        .oneOf(
          bartracks.flatMap((bartrack) => bartrack.bartrackId),
          t("bartrackUUID_invalid"),
        )
        .required(t("bartrackUUID_required")),
    }),
  );

  function handlePictureChange(event, isLine, index) {
    const file = event.target.files[0];

    if (file) {
      if (file.type.startsWith("image/")) {
        const url = URL.createObjectURL(file);
        setNewValue(isLine, index, "imageUrl", url);
      }
    }
  }

  function setNewValue(isLine, index, variableName, value) {
    if (isLine) {
      setLines((prevLines) => {
        const newLines = [...prevLines];
        newLines[index] = { ...newLines[index], [variableName]: value };
        return newLines;
      });
    } else {
      setBartracks((prevBartracks) => {
        const newBartracks = [...prevBartracks];
        newBartracks[index] = { ...newBartracks[index], [variableName]: value };
        return newBartracks;
      });
    }
  }

  const fetchBartrack = async (targetOrgId = currentOrgId) => {
    try {
      setIsLoading(true);
      setBartracks([]);
      setLines([]);

      let bartracks = await getCollection({
        collectionName: "Bartracks",
        orgId: targetOrgId,
      });

      bartracks = bartracks.data;

      const linesArrays = await Promise.all(
        bartracks.map(async (bartrack) => {
          const lines = await getBartrackLines({ bartrackId: bartrack?.id });
          return lines.map((line) => ({ ...line, bartrackUUID: bartrack?.bartrackId }));
        }),
      );

      const lines = linesArrays.flat();

      setBartracks(bartracks);
      setLines(lines);
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      return { bartracks: [], lines: [] };
    }
  };

  async function handleSubmit() {
    const errors = { bartracks: [], lines: [] };

    try {
      await bartracksValidation.validate(bartracks, { abortEarly: false });
    } catch (err) {
      err.inner.forEach((e) => {
        const index = parseInt(e.path.match(/\d+/)[0]);
        const field = e.path.split(".").pop();
        if (!errors.bartracks[index]) errors.bartracks[index] = {};
        errors.bartracks[index][field] = e.message;
      });
    }

    try {
      await linesValidation.validate(lines, { abortEarly: false });
    } catch (err) {
      err.inner.forEach((e) => {
        const index = parseInt(e.path.match(/\d+/)[0]);
        const field = e.path.split(".").pop();
        if (!errors.lines[index]) errors.lines[index] = {};
        errors.lines[index][field] = e.message;
      });
    }

    if (errors?.bartracks?.length === 0 && errors?.lines?.length === 0) {
      let bartracksFormatted;

      bartracksFormatted = bartracks.map((bartrack) => {
        const associatedLines = lines
          .filter((line) => line.bartrackUUID === bartrack.bartrackId)
          .map((line) => ({
            name: line.name,
            profitabilityIndex: line.profitabilityIndex ? Number(line.profitabilityIndex) : null,
            imageUrl: line.imageUrl ?? null,
            bartrackId: line.bartrackId,
            id: line.id,
          }));

        return {
          name: bartrack.name,
          imageUrl: bartrack.imageUrl ?? null,
          bartrackId: bartrack.bartrackId,
          orgId: currentOrgId,
          lines: associatedLines,
          id: bartrack.id,
          pressureLimit: bartrack.pressureLimit ? Number(bartrack.pressureLimit) : null,
        };
      });

      try {
        await updateBartracks({ bartracks: bartracksFormatted, orgId: currentOrgId });
        navigate("/dashboard/bartrack-overview");
      } catch (error) {
        console.error(error);
      }
    }

    setErrors(errors);
  }

  const checkNumberFormat = (number) => /^$|^[0-9]+([.,][0-9]{0,2})?$/.test(number);

  return (
    <div className="bartrack-setup">
      {isLoading && <Loader2 loader={isLoading} />}
      <div className="bartrack-setup-header">
        <button onClick={() => navigate(-1)}>{t("back")}</button>
        <h1>{t("bartrack_setup_title")}</h1>
      </div>
      {!isLoading && bartracks?.length > 0 && lines?.length > 0 && (
        <>
          <div className="bartrack-setup-bartracks">
            <h3>{t("bartracks_section_title")}</h3>
            {bartracks?.map((bartrack, index) => (
              <div key={bartrack.bartrackId} className="bartrack-setup-bartracks-item">
                <h5>Bartrack n°{bartrack.bartrackId}</h5>
                <div className="bartrack-setup-bartracks-fields">
                  <div className="bartrack-setup-bartracks-fields-texts">
                    <FormField
                      label={t("bartrack_name_label")}
                      secondaryTheme
                      onChange={(value) => setNewValue(false, index, "name", value)}
                      error={errors?.bartracks[index]?.name}
                      value={bartrack.name}
                    />
                    <FormField
                      label={t("pressure_bar_label")}
                      type="text"
                      secondaryTheme
                      onChange={(value) => {
                        const valueFormatted = value.replace(",", ".");
                        if (checkNumberFormat(valueFormatted)) {
                          setNewValue(
                            false,
                            index,
                            "pressureLimit",
                            valueFormatted.trim() === "" ? null : valueFormatted,
                          );
                        }
                      }}
                      error={errors?.bartracks[index]?.pressureLimit}
                      value={bartrack.pressureLimit ?? ""}
                    />
                  </div>
                  <div className="bartrack-setup-bartracks-fields-photo">
                    <p>{t("bartrack_photo_label")}</p>
                    <div className="bartrack-setup-bartracks-fields-photo-field">
                      <div className="bartrack-setup-bartracks-fields-photo-field-container">
                        <input
                          type="file"
                          id={`fileUpload-bartrack-${index}`}
                          style={{ display: "none" }}
                          onChange={(e) => handlePictureChange(e, false, index)}
                        />
                        {bartrack.imageUrl ? (
                          <img src={bartrack.imageUrl} />
                        ) : (
                          <p>{t("no_image")}</p>
                        )}
                      </div>
                      <GreenRedOutlinedButtons
                        greenOnPress={() =>
                          document.getElementById(`fileUpload-bartrack-${index}`).click()
                        }
                        redOnPress={() => setNewValue(false, index, "imageUrl", null)}
                        greenName={
                          bartrack.imageUrl ? t("bartrack_photo_modify") : t("bartrack_photo_add")
                        }
                        redName={t("bartrack_photo_delete")}
                        notDeletable={!bartrack.imageUrl}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="bartrack-setup-lines">
            <h3>{t("lines_section_title")}</h3>
            {lines?.map((line, index) => (
              <div key={line.bartrackId} className="bartrack-setup-lines-item">
                <h5>
                  {t("line_number")}
                  {line.bartrackId}
                </h5>
                <div className="bartrack-setup-lines-fields">
                  <div className="bartrack-setup-lines-fields-texts">
                    <FormField
                      label={t("line_name_label")}
                      secondaryTheme
                      onChange={(value) => setNewValue(true, index, "name", value)}
                      error={errors?.lines[index]?.name}
                      value={line.name}
                    />
                    <FormField
                      label={t("line_profitability_label")}
                      type="text"
                      secondaryTheme
                      onChange={(value) => {
                        const valueFormatted = value.replace(",", ".");
                        if (checkNumberFormat(valueFormatted)) {
                          setNewValue(
                            true,
                            index,
                            "profitabilityIndex",
                            valueFormatted.trim() === "" ? null : valueFormatted,
                          );
                        }
                      }}
                      error={errors?.lines[index]?.profitabilityIndex}
                      value={line.profitabilityIndex ?? ""}
                    />
                  </div>
                  <div className="bartrack-setup-lines-fields-photo">
                    <p>{t("bartrack_photo_label")}</p>
                    <div className="bartrack-setup-lines-fields-photo-field">
                      <div className="bartrack-setup-lines-fields-photo-field-container">
                        <input
                          type="file"
                          id={`fileUpload-line-${index}`}
                          style={{ display: "none" }}
                          onChange={(e) => handlePictureChange(e, true, index)}
                        />
                        {line.imageUrl ? <img src={line.imageUrl} /> : <p>{t("no_image")}</p>}
                      </div>
                      <GreenRedOutlinedButtons
                        greenOnPress={() =>
                          document.getElementById(`fileUpload-line-${index}`).click()
                        }
                        redOnPress={() => setNewValue(true, index, "imageUrl", null)}
                        greenName={
                          line.imageUrl ? t("bartrack_photo_modify") : t("bartrack_photo_add")
                        }
                        redName={t("bartrack_photo_delete")}
                        notDeletable={!line.imageUrl}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <GradientButton onPress={handleSubmit}>{t("save_button")}</GradientButton>
        </>
      )}
    </div>
  );
}

export default BartrackSetup;
